import { useEffect, useMemo, useRef, useState } from 'react'
import { getMailboxIndexing, startMailboxIndexing, Mailbox, MailApiError, MailIndexingStatus, MailIndexHistoryAction } from 'services/mail'

interface IndexingState {
  status: MailIndexingStatus | null
  loading: boolean
  pending: boolean
  valid: boolean
  error: string
  forbidden: boolean
  confirming: boolean
  preview: MailIndexHistoryAction | null
  notice: string
}
const empty: IndexingState = { status: null, loading: false, pending: false, valid: false, error: '', forbidden: false, confirming: false, preview: null, notice: '' }

/** Each selection owns its requests, timer and mutation state. */
export function useMailboxIndexing(box: Mailbox | null, selectionGeneration: number) {
  const id = box?.id
  const manage = !!box?.permissions['mail.manage']
  const context = useMemo(() => ({}), [id, box?.config_version, manage, selectionGeneration])
  const current = useRef(context)
  current.current = context
  const [stored, setStored] = useState({ context, value: empty })
  const commands = useRef({ refresh: () => {}, start: async () => {}, confirm: () => {}, close: () => {} })
  const postLock = useRef<object | null>(null)

  useEffect(() => {
    let cancelled = false, getting = false, posting = false, forbidden = false
    let timer: number | undefined
    let state = { ...empty }
    const active = () => !cancelled && current.current === context
    const update = (patch: Partial<IndexingState>) => {
      if (!active()) return
      state = { ...state, ...patch }
      setStored({ context, value: state })
    }
    const schedule = () => {
      window.clearTimeout(timer)
      if (active() && id && manage && !forbidden) timer = window.setTimeout(refresh, 10000)
    }
    const fail = (error: unknown, message: string) => {
      forbidden = [403, 404].includes((error as MailApiError)?.status)
      update({ error: forbidden ? `${message}. Accesso alla casella non consentito o revocato.` : message,
        forbidden, valid: false, ...(forbidden ? { confirming: false } : {}) })
    }
    const refresh = async () => {
      if (!active() || !id || !manage || getting) return
      window.clearTimeout(timer)
      getting = true
      update({ loading: true, valid: false })
      try {
        const status = await getMailboxIndexing(id)
        if (!active()) return
        forbidden = false
        update({ status, valid: true, forbidden: false, error: '',
          ...(state.confirming && !state.preview ? { preview: status.history_action } : {}) })
      } catch (error) {
        if (active()) fail(error, "Impossibile aggiornare lo stato dell'indicizzazione")
      } finally {
        getting = false
        if (active()) { update({ loading: false }); if (!posting) schedule() }
      }
    }
    const start = async () => {
      const preview = state.preview
      if (!active() || !id || !manage || posting || postLock.current === context || getting || forbidden || !state.valid || !state.status?.effective
        || !state.confirming || !preview?.allowed || !['start', 'resume'].includes(preview.kind)) return
      postLock.current = context
      posting = true
      window.clearTimeout(timer)
      update({ pending: true, error: '' })
      try {
        const { allowed, ...expected_action } = preview
        const result = await startMailboxIndexing(id, { expected_action })
        if (!active()) return
        update({ confirming: false, preview: null, valid: false, notice: {
          start: 'Storico accodato. Le elaborazioni proseguono in background.',
          resume: 'Ripresa dello storico accodata. Le elaborazioni proseguono in background.',
          existing: 'Storico già in lavorazione.', none: 'Nessuna email da elaborare.',
        }[result.action] })
        await refresh()
      } catch (error) {
        if (!active()) return
        const failure = error as MailApiError
        if (failure.status === 409 && failure.code === 'indexing_preview_changed') {
          update({ preview: null, valid: false, notice: 'Anteprima modificata. Verifica i conteggi aggiornati e conferma nuovamente.' })
          await refresh()
        } else if (!failure.status || failure.status >= 500) {
          update({ confirming: false, preview: null, valid: false,
            notice: 'Esito dell’avvio non confermato. Verifica lo stato aggiornato prima di una nuova azione.' })
          await refresh()
        } else fail(error, "Impossibile avviare l'indicizzazione dello storico")
      } finally {
        posting = false
        if (postLock.current === context) postLock.current = null
        if (active()) { update({ pending: false }); schedule() }
      }
    }
    commands.current = {
      refresh: () => { if (!posting) void refresh() }, start,
      confirm: () => {
        if (!active() || posting || getting || !state.valid || forbidden) return
        update({ confirming: true, preview: null, notice: '' })
        void refresh()
      },
      close: () => update({ confirming: false, preview: null }),
    }
    update(empty)
    if (id && manage) void refresh()
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [context, id, manage])

  const state = stored.context === context ? stored.value : empty
  return { ...state, refresh: () => commands.current.refresh(), start: () => commands.current.start(),
    confirm: () => commands.current.confirm(), close: () => commands.current.close() }
}
